import api from "./client";
import { openAuthenticatedPdf, downloadAuthenticatedFile } from "./pdf";

export const getClients = (params) => api.get("/clients", { params });
export const getClient = (clientId) => api.get(`/clients/${clientId}`);
export const createClient = (data) => api.post("/clients", data);
export const updateClient = (clientId, data) => api.patch(`/clients/${clientId}`, data);
export const deleteClient = (clientId) => api.delete(`/clients/${clientId}`);
// Remove a junk record (newsletter, listing portal) and block its senders.
export const markNotAClient = (clientId, reason) => api.post(`/clients/${clientId}/not-a-client`, { reason });
// Statement of account (ledger) PDF for the whole client or one department.
export const openClientStatementPdf = (clientId, department) => {
  const qs = department ? `?department=${encodeURIComponent(department)}` : "";
  return openAuthenticatedPdf(`/clients/${clientId}/statement.pdf${qs}`);
};
// Excel statement: all invoices and payments (with payment dates) for the client or one department.
export const downloadClientStatementXlsx = (clientId, department) => {
  const qs = department ? `?department=${encodeURIComponent(department)}` : "";
  return downloadAuthenticatedFile(`/clients/${clientId}/statement.xlsx${qs}`, "Statement of account.xlsx");
};
export const getClientActivity = (clientId) => api.get(`/clients/${clientId}/activity`);
export const getClientInsights = (clientId, department) => api.get(`/clients/${clientId}/insights`, { params: department ? { department } : {} });
export const getClientScoresBatch = () => api.get("/clients/scores/batch");
export const getClientsOverviewBatch = () => api.get("/clients/overview/batch");
export const scrapeClientWebsite = (clientId) => api.post(`/clients/${clientId}/scrape-website`);
export const lookupCompanyInfo = (clientId) => api.post(`/clients/${clientId}/lookup-company`);
export const resyncClients = () => api.post("/clients/generate-from-bookings");
export const getClientPipeline = () => api.get("/clients/pipeline");
export const updateClientPipelineStage = (id, data) => api.patch(`/clients/${id}/pipeline-stage`, data);
export const uploadClientContract = (id, formData) => api.post(`/clients/${id}/upload-contract`, formData, { headers: { "Content-Type": "multipart/form-data" } });
// Client Documents tab (ISS-fb8c4e71 / ISS-127baa18 / ISS-2400d9b8): any common document type.
export const getClientDocuments = (clientId) => api.get(`/clients/${clientId}/documents`);
// One file per request keeps each upload under the 25MB proxy limit.
export const uploadClientDocument = (clientId, file, { onUploadProgress } = {}) => {
  const fd = new FormData();
  fd.append("files", file);
  return api.post(`/clients/${clientId}/documents`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 180000,
    onUploadProgress,
  });
};
export const downloadClientDocument = (clientId, documentId) =>
  api.get(`/clients/${clientId}/documents/${encodeURIComponent(documentId)}/download`, { responseType: "blob", timeout: 180000 });
export const deleteClientDocument = (clientId, documentId) =>
  api.delete(`/clients/${clientId}/documents/${encodeURIComponent(documentId)}`);
export const getClientSuggestions = (params) => api.get("/clients/suggestions", { params });
export const dismissClientSuggestion = (id) => api.post(`/clients/${id}/dismiss-suggestion`);
// contact (optional) = the client contact the note is about (Fan, 2026-10-14)
export const addClientNote = (clientId, note, contact) => api.post(`/clients/${clientId}/notes`, {
  note,
  ...(contact ? { contact_id: contact.contact_id || undefined, contact_email: contact.email || undefined } : {}),
});
// True when a client note is about this contact (id first, email for older contacts)
export const noteIsAboutContact = (note, contact) => {
  if (!note || !contact) return false;
  if (note.contact_id && contact.contact_id) return note.contact_id === contact.contact_id;
  const ne = (note.contact_email || "").toLowerCase();
  return !!ne && ne === (contact.email || "").toLowerCase();
};
// note ref = note_id, or created_at for notes saved before note ids existed
export const clientNoteRef = (note) => note?.note_id || note?.created_at;
export const editClientNote = (clientId, noteRef, note) =>
  api.patch(`/clients/${clientId}/notes/${encodeURIComponent(noteRef)}`, { note });
export const deleteClientNote = (clientId, noteRef) =>
  api.delete(`/clients/${clientId}/notes/${encodeURIComponent(noteRef)}`);
export const getClientTimeline = (clientId, params) => api.get(`/clients/${clientId}/timeline`, { params });
export const getClientRelationshipDepth = (clientId, department) => api.get(`/clients/${clientId}/relationship-depth`, { params: department ? { department } : {} });
// Department settings: billing currency, VAT, budget and preference overrides
export const getClientDepartments = (clientId) => api.get(`/clients/${clientId}/departments`);
export const updateClientDepartment = (clientId, name, data) => api.patch(`/clients/${clientId}/departments/${encodeURIComponent(name)}`, data);
// Department's own invoice entity (legal name, address, VAT + Xero contact). body.mode: preview | apply.
export const applyDepartmentBillingEntity = (clientId, name, body) => api.post(`/clients/${clientId}/departments/${encodeURIComponent(name)}/billing-entity`, body);
export const clearDepartmentBillingEntity = (clientId, name) => api.delete(`/clients/${clientId}/departments/${encodeURIComponent(name)}/billing-entity`);
export const getClientResolvedPreferences = (clientId, department) => api.get(`/clients/${clientId}/resolved-preferences`, { params: department ? { department } : {} });
export const getClientDepartmentBreakdown = (clientId) => api.get(`/clients/${clientId}/department-breakdown`);
export const getClientBudgetProfile = (clientId, department) => api.get(`/clients/${clientId}/budget-profile`, { params: department ? { department } : {} });
export const getClientOrgChart = (clientId) => api.get(`/clients/${clientId}/org-chart`);
export const getClientContactCommunications = (clientId, params) =>
  api.get(`/clients/${clientId}/contact-communications`, { params });
// Merge
export const getDuplicateCandidates = (clientId) => api.get(`/clients/${clientId}/duplicate-candidates`);
export const getMergePreview = (clientId, otherId) => api.get(`/clients/${clientId}/merge-preview`, { params: { other_id: otherId } });
export const mergeClients = (clientId, otherId, acknowledgedIrreversible) => api.post(`/clients/${clientId}/merge`, { other_id: otherId, acknowledged_irreversible: acknowledgedIrreversible });
export const searchClientsForMerge = (query) => api.get("/clients", { params: { search: query, limit: 20 } });
// Outstanding invoices behind one aged balance bucket. Reads the same invoice
// set the buckets are computed from, so counts and lists cannot disagree.
export const getClientOutstandingInvoices = (clientId, params) =>
  api.get(`/clients/${clientId}/outstanding-invoices`, { params });

// Contacts of the client behind an invoice / booking / enquiry, for Cc pickers.
export const resolveClientContacts = (params) =>
  api.get("/clients/contacts/resolve", { params });
export const researchClient = (clientId) => api.post(`/clients/${clientId}/research`);
export const resolveClientResearchSuggestion = (clientId, action) => api.post(`/clients/${clientId}/research/suggestion`, { action });
export const refreshClientHqAddress = (clientId) => api.post(`/clients/${clientId}/hq-address/refresh`);
export const syncClientXeroContact = (clientId, body = {}) => api.post(`/clients/${clientId}/xero-contact/sync`, body);
// Add-only: POST /clients/{id}/contacts returns {added, skipped} (client Notes tab, 2026-10-16)
export const addClientContacts = (clientId, contacts) => api.post(`/clients/${clientId}/contacts`, { contacts });
